import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ArrowLeft, Trophy, MapPin, Calendar, Plus, X, Loader2, Trash2, AlertCircle, Pencil, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, type Event, type EventRegistration, type Member } from '@/lib/api';

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [regs, setRegs] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingReg, setEditingReg] = useState<EventRegistration | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await apiCall<{ event: Event; registrations: EventRegistration[] }>(`/v1/events/${id}`, { bearerToken: token });
      setEvent(data.event);
      setRegs(data.registrations);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken, id]);

  useEffect(() => { void load(); }, [load]);

  async function doDelete() {
    if (!event || !confirm(`Delete "${event.name}"? This removes all ${regs.length} registration(s).`)) return;
    const token = await getToken();
    await apiCall(`/v1/events/${event.id}`, { method: 'DELETE', bearerToken: token });
    navigate('/dashboard/events');
  }

  async function unregister(regId: string) {
    if (!confirm('Remove this registration?')) return;
    const token = await getToken();
    await apiCall(`/v1/events/registrations/${regId}`, { method: 'DELETE', bearerToken: token });
    void load();
  }

  if (loading) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (error) return <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error}</CardContent></Card>;
  if (!event) return null;

  const start = new Date(event.starts_at);
  const end = event.ends_at ? new Date(event.ends_at) : null;
  const past = start < new Date();
  const competed = regs.filter((r) => r.status === 'competed').length;
  const placed = regs.filter((r) => r.placement && r.placement <= 3).length;

  return (
    <div className="space-y-4 max-w-5xl">
      <Button variant="ghost" size="sm" asChild><Link to="/dashboard/events"><ArrowLeft className="mr-1 h-4 w-4" /> Events</Link></Button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Trophy className="h-6 w-6" /> {event.name}</h1>
          <div className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap mt-1">
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />
              {start.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              {end && end.toDateString() !== start.toDateString() && ` – ${end.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}`}
            </span>
            {event.location_name && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {event.location_name}</span>}
            {event.sanctioning_body && <Badge variant="outline">{event.sanctioning_body}</Badge>}
            <Badge variant={event.status === 'completed' ? 'outline' : event.status === 'cancelled' ? 'destructive' : 'default'}>{event.status}</Badge>
          </div>
          {event.url && (
            <a href={event.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Registration page
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)}><Plus className="mr-1 h-4 w-4" /> Add competitor</Button>
          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => void doDelete()}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="py-3"><div className="text-2xl font-bold">{regs.length}</div><div className="text-xs text-muted-foreground">registered</div></CardContent></Card>
        <Card><CardContent className="py-3"><div className="text-2xl font-bold">{competed}</div><div className="text-xs text-muted-foreground">competed</div></CardContent></Card>
        <Card><CardContent className="py-3"><div className="text-2xl font-bold">{placed}</div><div className="text-xs text-muted-foreground">placed top-3</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {regs.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No competitors yet. Click <strong>Add competitor</strong> to register a member.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b"><tr>
                <th className="text-left px-4 py-2 font-medium">Member</th>
                <th className="text-left px-4 py-2 font-medium">Weight</th>
                <th className="text-left px-4 py-2 font-medium">Bracket</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Record</th>
                <th className="text-left px-4 py-2 font-medium">Result</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr></thead>
              <tbody>{regs.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-4 py-2"><Link to={`/dashboard/members/${r.member_id}`} className="hover:underline font-medium">{r.member_name}</Link></td>
                  <td className="px-4 py-2 tabular-nums">{r.weight_class ?? '—'}</td>
                  <td className="px-4 py-2">{r.bracket ?? '—'}</td>
                  <td className="px-4 py-2"><Badge variant={r.status === 'competed' ? 'default' : r.status === 'withdrew' ? 'destructive' : 'outline'}>{r.status}</Badge></td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.wins != null || r.losses != null ? `${r.wins ?? 0}-${r.losses ?? 0}` : '—'}</td>
                  <td className="px-4 py-2 text-xs">{r.placement ? <span className="font-medium">{ordinal(r.placement)}</span> : null}{r.placement && r.result ? ' · ' : ''}{r.result ?? (r.placement ? '' : '—')}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingReg(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive" onClick={() => void unregister(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {event.notes && <Card><CardContent className="py-3 text-sm whitespace-pre-wrap">{event.notes}</CardContent></Card>}

      {showAdd && event && (
        <AddCompetitorDialog
          eventId={event.id}
          alreadyRegistered={new Set(regs.map((r) => r.member_id))}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); void load(); }}
        />
      )}
      {editingReg && (
        <EditRegistrationDialog
          reg={editingReg}
          isPast={past}
          onClose={() => setEditingReg(null)}
          onSaved={() => { setEditingReg(null); void load(); }}
        />
      )}
    </div>
  );
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? 'th');
}

function AddCompetitorDialog({
  eventId, alreadyRegistered, onClose, onAdded,
}: { eventId: string; alreadyRegistered: Set<string>; onClose: () => void; onAdded: () => void }) {
  const { getToken } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [memberId, setMemberId] = useState('');
  const [weightClass, setWeightClass] = useState('');
  const [bracket, setBracket] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await getToken();
      const data = await apiCall<{ items: Member[] }>('/v1/members', {
        bearerToken: token,
        query: { status: 'active', search: search || undefined, limit: 100 },
      });
      setMembers(data.items);
    })();
  }, [getToken, search]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      await apiCall(`/v1/events/${eventId}/registrations`, {
        method: 'POST',
        bearerToken: token,
        body: { member_id: memberId, weight_class: weightClass.trim() || null, bracket: bracket.trim() || null },
      });
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add competitor</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Search members</span>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a name…" className="w-full border rounded px-2 py-1 text-sm" />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Member *</span>
              <select value={memberId} onChange={(e) => setMemberId(e.target.value)} required className="w-full border rounded px-2 py-1 text-sm">
                <option value="">— Select —</option>
                {members.filter((m) => !alreadyRegistered.has(m.id)).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Weight class</span>
                <input value={weightClass} onChange={(e) => setWeightClass(e.target.value)} placeholder="106 lb" className="w-full border rounded px-2 py-1 text-sm" />
              </label>
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Bracket</span>
                <input value={bracket} onChange={(e) => setBracket(e.target.value)} placeholder="Varsity / JV / Novice" className="w-full border rounded px-2 py-1 text-sm" />
              </label>
            </div>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!memberId || submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Register
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}

function EditRegistrationDialog({
  reg, isPast, onClose, onSaved,
}: { reg: EventRegistration; isPast: boolean; onClose: () => void; onSaved: () => void }) {
  const { getToken } = useAuth();
  const [status, setStatus] = useState<EventRegistration['status']>(reg.status);
  const [weightClass, setWeightClass] = useState(reg.weight_class ?? '');
  const [bracket, setBracket] = useState(reg.bracket ?? '');
  const [wins, setWins] = useState(reg.wins?.toString() ?? '');
  const [losses, setLosses] = useState(reg.losses?.toString() ?? '');
  const [placement, setPlacement] = useState(reg.placement?.toString() ?? '');
  const [result, setResult] = useState(reg.result ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      const body: Record<string, unknown> = {
        status,
        weight_class: weightClass.trim() || null,
        bracket: bracket.trim() || null,
        wins: wins ? parseInt(wins, 10) : null,
        losses: losses ? parseInt(losses, 10) : null,
        placement: placement ? parseInt(placement, 10) : null,
        result: result.trim() || null,
      };
      await apiCall(`/v1/events/registrations/${reg.id}`, { method: 'PATCH', bearerToken: token, body });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{reg.member_name}</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as EventRegistration['status'])} className="w-full border rounded px-2 py-1 text-sm">
                <option value="registered">registered</option>
                <option value="competed">competed</option>
                <option value="withdrew">withdrew</option>
                <option value="cancelled">cancelled</option>
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Weight class</span>
                <input value={weightClass} onChange={(e) => setWeightClass(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
              </label>
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Bracket</span>
                <input value={bracket} onChange={(e) => setBracket(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
              </label>
            </div>
            {isPast && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <label className="block text-sm">
                    <span className="block text-xs text-muted-foreground mb-1">Wins</span>
                    <input type="number" min="0" value={wins} onChange={(e) => setWins(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                  </label>
                  <label className="block text-sm">
                    <span className="block text-xs text-muted-foreground mb-1">Losses</span>
                    <input type="number" min="0" value={losses} onChange={(e) => setLosses(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                  </label>
                  <label className="block text-sm">
                    <span className="block text-xs text-muted-foreground mb-1">Place</span>
                    <input type="number" min="1" value={placement} onChange={(e) => setPlacement(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">Result notes</span>
                  <input value={result} onChange={(e) => setResult(e.target.value)} placeholder="e.g. 'lost in semifinals'" className="w-full border rounded px-2 py-1 text-sm" />
                </label>
              </>
            )}
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
